import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import CandidateAutocomplete from '../components/CandidateAutocomplete';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin — full access' },
  { value: 'campaign_manager', label: 'Campaign Manager — assigned candidates only' },
];

const EMPTY = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  role: 'campaign_manager',
  password: '',
  is_active: true,
  assigned_candidate_id: '',
};

export default function UserFormPage({ mode = 'create' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [confirmPwd, setConfirmPwd] = useState('');
  // Stash the assigned-candidate display label so the autocomplete can show
  // it without re-fetching the candidate. The backend already returns this
  // on the user payload as `assigned_candidate`.
  const [assignedLabel, setAssignedLabel] = useState('');

  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/users/${id}`);
        if (active) {
          const incoming = { ...EMPTY, ...res.data, password: '' };
          incoming.assigned_candidate_id = res.data.assigned_candidate_id || '';
          setData(incoming);
          const c = res.data.assigned_candidate;
          if (c) {
            const meta = [c.office, c.state && `(${c.state})`].filter(Boolean).join(' · ');
            setAssignedLabel(meta ? `${c.full_name} — ${meta}` : c.full_name);
          }
        }
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, mode]);

  function update(field, value) {
    setData((d) => {
      const next = { ...d, [field]: value };
      // Clearing assignment automatically when role is no longer manager
      // keeps state honest.
      if (field === 'role' && value !== 'campaign_manager') {
        next.assigned_candidate_id = '';
      }
      return next;
    });
  }

  function validate() {
    const errs = {};
    if (!data.email?.trim()) errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = 'Invalid email';
    if (!data.first_name?.trim()) errs.first_name = 'Required';
    if (!data.last_name?.trim()) errs.last_name = 'Required';
    if (!data.phone?.trim()) errs.phone = 'Required';
    if (!data.role) errs.role = 'Required';
    if (mode === 'create') {
      if (!data.password || data.password.length < 6) errs.password = 'Minimum 6 characters';
      if (data.password && data.password !== confirmPwd) errs.confirm = 'Passwords do not match';
    } else if (data.password) {
      if (data.password.length < 6) errs.password = 'Minimum 6 characters';
      if (data.password !== confirmPwd) errs.confirm = 'Passwords do not match';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) { setError('Please correct the highlighted fields.'); return; }
    setSaving(true);
    try {
      const payload = {
        email: data.email.trim().toLowerCase(),
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        phone: data.phone.trim(),
        role: data.role,
      };
      if (data.password) payload.password = data.password;
      if (mode === 'edit') payload.is_active = !!data.is_active;
      // Always send the candidate assignment on save so the backend can
      // sync (including clearing it when the role flips to admin). Empty
      // string is the sentinel for "clear".
      payload.assigned_candidate_id = data.role === 'campaign_manager'
        ? (data.assigned_candidate_id || '')
        : '';

      if (mode === 'create') {
        const res = await adminApi.post('/admin/users', payload);
        toast.success(`Created ${res.data.email}`);
        navigate('/admin/users');
      } else {
        await adminApi.put(`/admin/users/${id}`, payload);
        toast.success('User updated');
        navigate('/admin/users');
      }
    } catch (err) {
      const msg = extractError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={mode === 'create' ? 'New user' : 'Edit user'} />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'New user' : `Edit — ${data.full_name || data.email}`}
        breadcrumb={<span><Link to="/admin/users" className="text-decoration-none">Users</Link> / {mode === 'create' ? 'New' : 'Edit'}</span>}
        actions={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate('/admin/users')}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="user-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger" data-testid="user-form-error">{error}</Alert>}
        <Form noValidate onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <Card>
                <Card.Body>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 14 }}>Profile</h3>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>First name <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.first_name} value={data.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="user-first-name-input" />
                        <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Last name <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.last_name} value={data.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="user-last-name-input" />
                        <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="email" isInvalid={!!errors.email} value={data.email} onChange={(e) => update('email', e.target.value)} data-testid="user-email-input" />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.phone} value={data.phone} onChange={(e) => update('phone', e.target.value)} placeholder="e.g. 555-0101" data-testid="user-phone-input" />
                        <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr className="my-4" />
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 14 }}>
                    {mode === 'create' ? 'Password' : 'Change password'}
                  </h3>
                  {mode === 'edit' && (
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>Leave both fields blank to keep the current password.</p>
                  )}
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>{mode === 'create' ? 'Password' : 'New password'} {mode === 'create' && <span className="text-danger">*</span>}</Form.Label>
                        <Form.Control type="password" isInvalid={!!errors.password} value={data.password} onChange={(e) => update('password', e.target.value)} autoComplete="new-password" data-testid="user-password-input" />
                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Confirm</Form.Label>
                        <Form.Control type="password" isInvalid={!!errors.confirm} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} autoComplete="new-password" data-testid="user-password-confirm-input" />
                        <Form.Control.Feedback type="invalid">{errors.confirm}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card style={{ position: 'sticky', top: 12 }}>
                <Card.Header>Role &amp; access</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                    <Form.Select value={data.role} onChange={(e) => update('role', e.target.value)} data-testid="user-role-select">
                      {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                    <Form.Text>
                      Admins can manage the entire app. Campaign managers can only view and edit candidates they&apos;re assigned to.
                    </Form.Text>
                  </Form.Group>

                  {data.role === 'campaign_manager' && (
                    <Form.Group className="mb-3" data-testid="user-candidate-assignment-group">
                      <Form.Label>Manages candidate</Form.Label>
                      <CandidateAutocomplete
                        value={data.assigned_candidate_id || ''}
                        initialLabel={assignedLabel}
                        testid="user-candidate-autocomplete"
                        placeholder="Search candidates by name…"
                        onChange={(id, candidate) => {
                          update('assigned_candidate_id', id || '');
                          if (candidate) {
                            const meta = [candidate.office, candidate.state && `(${candidate.state})`].filter(Boolean).join(' · ');
                            setAssignedLabel(meta ? `${candidate.full_name} — ${meta}` : candidate.full_name);
                          } else {
                            setAssignedLabel('');
                          }
                        }}
                      />
                      <Form.Text>
                        Each campaign manager can be assigned to one candidate at a time. Changing this re-assigns them from any previous candidate.
                      </Form.Text>
                    </Form.Group>
                  )}

                  {mode === 'edit' && (
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="user-active-toggle"
                        label={data.is_active ? 'Account is active' : 'Account is disabled'}
                        checked={!!data.is_active}
                        onChange={(e) => update('is_active', e.target.checked)}
                        data-testid="user-active-toggle"
                      />
                    </Form.Group>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
}
